/**
 * Lazy Video Loader
 * Efficiently loads videos when they enter the viewport
 */
(() => {
  'use strict';

  class LazyVideoLoader {
    constructor() {
      this.observer = null;
      this.defaultRootMargin = '200px';
      
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupObserver());
      } else {
        this.setupObserver();
      }
    }

    setupObserver() {
      const videos = document.querySelectorAll('[data-lazy-video]');
      
      if (!videos.length) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => this.handleIntersection(entries),
          {
            rootMargin: this.defaultRootMargin,
            threshold: 0.1
          }
        );

        videos.forEach(video => this.observer.observe(video));
      } else {
        videos.forEach(video => this.loadVideo(video));
      }
    }

    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadVideo(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }

    loadVideo(video) {
      const shouldAutoplay = video.dataset.autoplay === 'true';
      const sources = video.querySelectorAll('source[data-src]');

      sources.forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });

      this.setupVideoEvents(video, shouldAutoplay);

      video.load();
    }

    setupVideoEvents(video, shouldAutoplay) {
      if (shouldAutoplay) {
        video.addEventListener('canplay', () => {
          this.attemptAutoplay(video);
        }, { once: true });

        document.addEventListener('click', () => this.attemptAutoplay(video), { once: true });
        document.addEventListener('touchstart', () => this.attemptAutoplay(video), { once: true });
      }
    }

    async attemptAutoplay(video) {
      try {
        await video.play();
      } catch (error) {
        if (error.name !== 'NotAllowedError') {
          console.warn('Video autoplay failed:', error);
        }
      }
    }
  }

  new LazyVideoLoader();
})();